import { useState, useEffect } from 'react';
import PVSHomePage from './PVSHomePage';
import companyLogo from '../assets/company-logo.png';
import { isTokenExpired, clearSession, consumeSessionExpiredFlag } from '../utils/session';

const LoginPage = () => {
 const [email, setEmail] = useState('');
 const [password, setPassword] = useState('');
 const [view, setView] = useState('login');
 const [submitted, setSubmitted] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Restore an existing session on mount so a page reload (e.g. a service
  // worker update or an incidental refresh) keeps the user logged in instead
  // of dropping them back to the login screen.
  useEffect(() => {
    try {
      // If we were bounced here by a 401 (expired/invalid token), tell the user.
      if (consumeSessionExpiredFlag()) {
        setErrors(prev => ({ ...prev, auth: 'Your session has expired. Please sign in again.' }));
      }

      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      if (token && storedUser) {
        // Don't restore an expired token — that leaves the user "logged in"
        // while every API call fails with 401.
        if (isTokenExpired(token)) {
          clearSession();
          setErrors(prev => ({ ...prev, auth: 'Your session has expired. Please sign in again.' }));
          return;
        }
        const user = JSON.parse(storedUser);
        setIsAdmin(user.isAdmin === true);
        setView('pvs-home');
      }
    } catch (error) {
      console.error('Error restoring session:', error);
    }
  }, []);
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    auth: ''
  });

  const handleLogout = () => {
    clearSession();
    setIsAdmin(false);
    setView('login');
    setEmail('');
    setPassword('');
    setErrors({ email: '', password: '', auth: '' });
  };

 const validateEmail = (emailOrUsername) => {
   if (!emailOrUsername) {
     return 'Email or username is required';
   }
   if (emailOrUsername.includes('@')) {
     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
     return !emailRegex.test(emailOrUsername) ? 'Please enter a valid email address' : '';
   }
   if (emailOrUsername.length < 3) {
     return 'Username must be at least 3 characters';
   }
   if (emailOrUsername.length > 20) {
     return 'Username must be 20 characters or less';
   }
   if (!/^[a-zA-Z0-9_]+$/.test(emailOrUsername)) {
     return 'Username can only contain letters, numbers, and underscores';
   }
   return '';
 };

 const validatePassword = (password) => {
   return !password ? 'Password is required' :
          password.length < 6 ? 'Password must be at least 6 characters' : '';
 };

 const handleEmailChange = (e) => {
   const value = e.target.value;
   setEmail(value);
   setErrors(prev => ({ ...prev, email: validateEmail(value), auth: '' }));
 };

 const handlePasswordChange = (e) => {
   const value = e.target.value;
   setPassword(value);
   setErrors(prev => ({ ...prev, password: validatePassword(value), auth: '' }));
 };

 const handleLogin = async (e) => {
   e.preventDefault();
   const emailError = validateEmail(email);
   const passwordError = validatePassword(password);
   setErrors({ email: emailError, password: passwordError, auth: '' });

   if (!emailError && !passwordError) {
     try {
       const response = await fetch('https://app.pvslimited.co.uk/api/auth/login', {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify({ email, password })
       });
       const data = await response.json();
       if (!response.ok) {
         if (response.status === 423) {
           setErrors(prev => ({ ...prev, auth: 'Account temporarily locked due to too many failed attempts. Try again later.' }));
         } else if (response.status === 429) {
           setErrors(prev => ({ ...prev, auth: 'Too many login attempts. Please try again later.' }));
         } else {
           setErrors(prev => ({ ...prev, auth: data.error || 'Invalid email/username or password' }));
         }
         return;
       }
       localStorage.setItem('token', data.token);
       localStorage.setItem('user', JSON.stringify(data.user));
       setIsAdmin(data.user.isAdmin);
       setView('pvs-home');
       console.log(`✅ Login successful: ${data.user.email} (${data.user.username})`);
     } catch (error) {
       console.error('Login error:', error);
       setErrors(prev => ({ ...prev, auth: 'Connection error. Please check your internet and try again.' }));
     }
   }
 };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    const emailError = validateEmail(email);
    setErrors(prev => ({ ...prev, email: emailError }));
    if (!emailError) {
      console.log('Password reset requested for:', email);
      setSubmitted(true);
    }
  };

  const renderForgotPassword = () => (
    <div className="min-h-screen bg-pvs-bg flex flex-col justify-center relative overflow-hidden">
      {/* SVG gradient for two-tone accents */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="login-two-tone" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4ade80" />
            <stop offset="50%" stopColor="#a3e635" />
            <stop offset="100%" stopColor="#ffd700" />
          </linearGradient>
        </defs>
      </svg>

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <img src={companyLogo} alt="Company Logo" className="mx-auto h-64 w-auto" />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
          Reset your password
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="pvs-glass py-8 px-4 sm:px-10">
          {!submitted ? (
            <form className="space-y-6" onSubmit={handleForgotPassword}>
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-pvs-text-muted">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="reset-email" name="email" type="email" autoComplete="email" required
                    className={`pvs-input w-full ${errors.email ? 'border-red-500' : ''}`}
                    value={email} onChange={handleEmailChange}
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email}</p>}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <button type="button"
                  className="text-sm font-medium text-pvs-accent hover:text-pvs-accent-warm"
                  onClick={() => { setView('login'); setSubmitted(false); setErrors({ email: '', password: '', auth: '' }); }}
                >
                  Back to login
                </button>
                <button type="submit" disabled={!!errors.email}
                  className={`ml-3 inline-flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium focus:outline-none ${
                    errors.email ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'pvs-btn-accent'
                  }`}
                >
                  Reset Password
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center">
              <p className="text-sm text-pvs-text-muted">
                If an account exists with that email, we've sent password reset instructions.
              </p>
              <button
                className="mt-4 text-sm font-medium text-pvs-accent hover:text-pvs-accent-warm"
                onClick={() => { setView('login'); setSubmitted(false); setErrors({ email: '', password: '', auth: '' }); }}
              >
                Return to login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderLoginForm = () => (
    <div className="min-h-screen bg-pvs-bg flex flex-col justify-center relative overflow-hidden">
      {/* SVG gradient for two-tone accents */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="login-two-tone" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4ade80" />
            <stop offset="50%" stopColor="#a3e635" />
            <stop offset="100%" stopColor="#ffd700" />
          </linearGradient>
        </defs>
      </svg>

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <img src={companyLogo} alt="Company Logo" className="mx-auto h-64 w-auto" />
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="pvs-glass py-8 px-4 sm:px-10">
          <form className="space-y-6" onSubmit={handleLogin}>
            {errors.auth && (
              <div className="rounded-lg bg-red-900/30 border border-red-500/30 p-4">
                <p className="text-sm text-red-400">{errors.auth}</p>
              </div>
            )}
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-pvs-text-muted">
                Email Address or Username
              </label>
              <div className="mt-1">
                <input
                  id="email" name="email" type="text" autoComplete="email" required
                  placeholder="Enter email or username"
                  className={`pvs-input w-full ${errors.email ? 'border-red-500' : ''}`}
                  value={email} onChange={handleEmailChange}
                />
                {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-pvs-text-muted">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password" name="password" type="password" autoComplete="current-password" required
                  className={`pvs-input w-full ${errors.password ? 'border-red-500' : ''}`}
                  value={password} onChange={handlePasswordChange}
                />
                {errors.password && <p className="mt-1 text-sm text-red-400">{errors.password}</p>}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button type="button"
                className="text-sm font-medium text-pvs-accent hover:text-pvs-accent-warm"
                onClick={() => setView('forgot-password')}
              >
                Forgot your password?
              </button>
            </div>

            <div>
              <button type="submit" disabled={!!errors.email || !!errors.password}
                className={`w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold focus:outline-none ${
                  errors.email || errors.password
                    ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                    : 'pvs-btn-accent'
                }`}
              >
                Sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  switch (view) {
    case 'forgot-password':
      return renderForgotPassword();
    case 'pvs-home':
      return <PVSHomePage isAdmin={isAdmin} onLogout={handleLogout} />;
    default:
      return renderLoginForm();
  }
};

export default LoginPage;
